import i18n from '../i18n'
import styled from 'styled-components'
import * as FiIcons from 'react-icons/fi'

/**
 * ReliabilityIndicator Component
 *
 * Displays customer reliability index with score (0-100) and classification (A/B/C/D)
 * Includes detailed breakdown of payment metrics
 *
 * @example
 * <ReliabilityIndicator
 *   score={85}
 *   classification="A"
 *   metrics={{
 *     totalInvoices: 20,
 *     onTimePayments: 18,
 *     onTimePercentage: 90,
 *     avgDelayDays: 5,
 *     unpaidCount: 0,
 *     partiallyPaidCount: 2
 *   }}
 *   loading={false}
 * />
 */

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const MainIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const ScoreContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const ScoreLabel = styled.span`
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: ${props => props.theme.typography.fontSize.label};
  font-weight: 500;
  color: ${props => props.theme.colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: color 0.3s ease;
`

const ScoreBar = styled.div`
  position: relative;
  width: 100%;
  height: 32px;
  background: ${props => props.theme.colors.background.secondary};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.border.medium};
  overflow: hidden;
  transition: all 0.3s ease;
`

const ScoreFill = styled.div`
  height: 100%;
  width: ${props => props.score || 0}%;
  background: ${props => {
    if (props.score >= 85) return '#22c55e' // A - green
    if (props.score >= 70) return '#f59e0b' // B - orange
    if (props.score >= 50) return '#fb923c' // C - light orange
    return '#ef4444' // D - red
  }};
  transition: width 0.6s ease, background 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 0.75rem;
`

const ScoreText = styled.span`
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`

const ClassificationBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  height: 80px;
  border-radius: 12px;
  background: ${props => {
    if (props.classification === 'A') return '#f0fdf4'
    if (props.classification === 'B') return '#fffbeb'
    if (props.classification === 'C') return '#fff7ed'
    if (props.classification === 'D') return '#fef2f2'
    return '#f0f0f0'
  }};
  border: 2px solid ${props => {
    if (props.classification === 'A') return '#22c55e'
    if (props.classification === 'B') return '#f59e0b'
    if (props.classification === 'C') return '#fb923c'
    if (props.classification === 'D') return '#ef4444'
    return '#e0e0e0'
  }};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    min-width: 100%;
    height: 60px;
  }
`

const ClassificationLetter = styled.span`
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 2rem;
  font-weight: 700;
  color: ${props => {
    if (props.classification === 'A') return '#22c55e'
    if (props.classification === 'B') return '#f59e0b'
    if (props.classification === 'C') return '#fb923c'
    if (props.classification === 'D') return '#ef4444'
    return '#666'
  }};
  line-height: 1;
`

const ClassificationLabel = styled.span`
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 0.625rem;
  font-weight: 500;
  color: ${props => {
    if (props.classification === 'A') return '#22c55e'
    if (props.classification === 'B') return '#f59e0b'
    if (props.classification === 'C') return '#fb923c'
    if (props.classification === 'D') return '#ef4444'
    return '#666'
  }};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 0.25rem;
`

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid ${props => props.theme.colors.border.light};
  transition: border-color 0.3s ease;
`

const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const MetricLabel = styled.span`
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 0.625rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: color 0.3s ease;
`

const MetricValue = styled.span`
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.color || props.theme.colors.text.primary};
  transition: color 0.3s ease;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem;
  text-align: center;
  color: ${props => props.theme.colors.text.muted};
  background: ${props => props.theme.colors.background.secondary};
  border-radius: 8px;
  border: 1px dashed ${props => props.theme.colors.border.medium};
  transition: all 0.3s ease;
`

const EmptyIcon = styled.div`
  font-size: 2rem;
  opacity: 0.5;
`

const EmptyText = styled.span`
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: ${props => props.theme.typography.fontSize.input};
  font-style: italic;
`

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: ${props => props.theme.colors.text.muted};
  font-size: ${props => props.theme.typography.fontSize.input};
`

// Helper function to get classification description
const getClassificationDescription = (classification) => {
  switch (classification) {
    case 'A':
      return i18n.t('affidabilitaOttimo', { ns: 'clienti' })
    case 'B':
      return i18n.t('affidabilitaBuono', { ns: 'clienti' })
    case 'C':
      return i18n.t('affidabilitaMedio', { ns: 'clienti' })
    case 'D':
      return i18n.t('affidabilitaScarso', { ns: 'clienti' })
    default:
      return 'N/A'
  }
}

// Main Component
const ReliabilityIndicator = ({
  score = null,
  classification = 'N/A',
  metrics = null,
  loading = false,
  message = null
}) => {
  // Loading state
  if (loading) {
    return (
      <Container>
        <LoadingState>
          <FiIcons.FiRefreshCw className='spin' /> {i18n.t('loading', { ns: 'common' })}
        </LoadingState>
      </Container>
    )
  }

  // Empty state (no history)
  if (score === null || classification === 'N/A') {
    return (
      <Container>
        <EmptyState>
          <EmptyIcon>
            <FiIcons.FiInfo />
          </EmptyIcon>
          <EmptyText>
            {message || i18n.t('affidabilitaNessunStorico', { ns: 'clienti' })}
          </EmptyText>
        </EmptyState>
      </Container>
    )
  }

  // Main display
  return (
    <Container>
      <MainIndicator>
        <ScoreContainer>
          <ScoreLabel>{i18n.t('affidabilitaPunteggio', { ns: 'clienti' })}</ScoreLabel>
          <ScoreBar>
            <ScoreFill score={score}>
              {score >= 20 && <ScoreText>{score}/100</ScoreText>}
            </ScoreFill>
          </ScoreBar>
        </ScoreContainer>

        <ClassificationBadge classification={classification}>
          <ClassificationLetter classification={classification}>
            {classification}
          </ClassificationLetter>
          <ClassificationLabel classification={classification}>
            {getClassificationDescription(classification)}
          </ClassificationLabel>
        </ClassificationBadge>
      </MainIndicator>

      {metrics && (
        <MetricsGrid>
          <MetricItem>
            <MetricLabel>{i18n.t('affidabilitaTotaleFatture', { ns: 'clienti' })}</MetricLabel>
            <MetricValue>{metrics.totalInvoices}</MetricValue>
          </MetricItem>

          <MetricItem>
            <MetricLabel>{i18n.t('affidabilitaPagamentiPuntuali', { ns: 'clienti' })}</MetricLabel>
            <MetricValue color='#22c55e'>
              {metrics.onTimePayments} ({metrics.onTimePercentage}%)
            </MetricValue>
          </MetricItem>

          <MetricItem>
            <MetricLabel>{i18n.t('affidabilitaRitardoMedio', { ns: 'clienti' })}</MetricLabel>
            <MetricValue color={metrics.avgDelayDays > 30 ? '#ef4444' : metrics.avgDelayDays > 0 ? '#f59e0b' : '#22c55e'}>
              {metrics.avgDelayDays} {i18n.t('affidabilitaGiorni', { ns: 'clienti' })}
            </MetricValue>
          </MetricItem>

          {metrics.unpaidCount > 0 && (
            <MetricItem>
              <MetricLabel>{i18n.t('affidabilitaFattureInsolute', { ns: 'clienti' })}</MetricLabel>
              <MetricValue color='#ef4444'>
                {metrics.unpaidCount}
              </MetricValue>
            </MetricItem>
          )}

          {metrics.partiallyPaidCount > 0 && (
            <MetricItem>
              <MetricLabel>{i18n.t('affidabilitaPagamentiParziali', { ns: 'clienti' })}</MetricLabel>
              <MetricValue color='#f59e0b'>
                {metrics.partiallyPaidCount}
              </MetricValue>
            </MetricItem>
          )}
        </MetricsGrid>
      )}
    </Container>
  )
}

export default ReliabilityIndicator
